/**
 * Documents tab — job files with document type and serial number linking.
 * Phase 25: Upload with document_type + serial number selection.
 */

import { useCallback, useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { fetchFiles, uploadFile, uploadFileFromUrl, updateFile, deleteFile, openFileInTab, fetchSerialNumbers } from '../../../api/files';
import { Button, LoadingSpinner, EmptyState } from '../../common';

const DOCUMENT_TYPES = [
  { value: '', label: '— No type —' },
  { value: 'calibration_cert', label: 'Calibration Certificate' },
  { value: 'pull_test_cert', label: 'Pull Test Certificate' },
  { value: 'inspection_report', label: 'Inspection Report' },
  { value: 'material_cert', label: 'Material Cert / Mill Cert' },
  { value: 'drawing', label: 'Drawing' },
  { value: 'spec_sheet', label: 'Spec Sheet' },
  { value: 'other', label: 'Other' },
];

function fmtDate(d) {
  if (!d) return '—';
  try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return d; }
}

function formatFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function DocumentsTab({ job, onRefresh }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [jobSerials, setJobSerials] = useState([]);

  // Drag and drop
  const [dragOver, setDragOver] = useState(false);

  // Upload form state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [docType, setDocType] = useState('');
  const [docTypeCustom, setDocTypeCustom] = useState('');
  const [selectedSerials, setSelectedSerials] = useState([]);

  // Edit metadata on existing file
  const [editingFileId, setEditingFileId] = useState(null);
  const [editDocType, setEditDocType] = useState('');
  const [editDocTypeCustom, setEditDocTypeCustom] = useState('');
  const [editSerials, setEditSerials] = useState([]);

  const loadFiles = useCallback(async () => {
    try {
      const data = await fetchFiles({ job: job.id });
      setFiles(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [job.id]);

  const loadSerials = useCallback(async () => {
    try {
      const data = await fetchSerialNumbers({ job: job.id });
      setJobSerials(data);
    } catch {
      // ignore
    }
  }, [job.id]);

  useEffect(() => { loadFiles(); loadSerials(); }, [loadFiles, loadSerials]);

  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setShowUploadForm(true);
    setDocType('');
    setDocTypeCustom('');
    setSelectedSerials([]);
    e.target.value = '';
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    // Check for dropped files first
    if (e.dataTransfer?.files?.length > 0) {
      const file = e.dataTransfer.files[0];
      setPendingFile(file);
      setShowUploadForm(true);
      setDocType('');
      setDocTypeCustom('');
      setSelectedSerials([]);
      return;
    }

    // Check for dropped URL (dragged link from browser)
    const url = e.dataTransfer?.getData('text/uri-list') || e.dataTransfer?.getData('text/plain') || '';
    const trimmedUrl = url.trim().split('\n')[0].trim();
    if (trimmedUrl && /^https?:\/\//i.test(trimmedUrl)) {
      doUploadFromUrl(trimmedUrl);
    }
  }

  async function doUploadFromUrl(url) {
    setUploading(true);
    try {
      await uploadFileFromUrl({ url, job: job.id });
      loadFiles();
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to upload from URL');
    } finally {
      setUploading(false);
    }
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }

  async function handleUpload() {
    if (!pendingFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', pendingFile);
      formData.append('job', job.id);
      formData.append('filename', pendingFile.name);
      if (docType) formData.append('document_type', docType);
      if (docType === 'other' && docTypeCustom) formData.append('document_type_custom', docTypeCustom);
      if (selectedSerials.length > 0) {
        formData.append('serial_numbers', JSON.stringify(selectedSerials));
      }
      await uploadFile(formData);
      setPendingFile(null);
      setShowUploadForm(false);
      loadFiles();
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  function cancelUpload() {
    setPendingFile(null);
    setShowUploadForm(false);
  }

  async function handleDelete(fileId) {
    if (!confirm('Delete this file?')) return;
    try {
      await deleteFile(fileId);
      loadFiles();
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed');
    }
  }

  async function handleDownload(file) {
    try {
      await openFileInTab(file.id);
    } catch {
      alert('Failed to open file');
    }
  }

  function startEditFile(file) {
    setEditingFileId(file.id);
    setEditDocType(file.document_type || '');
    setEditDocTypeCustom(file.document_type_custom || '');
    setEditSerials((file.serial_numbers_display || []).map((sn) => sn.id));
  }

  async function saveEditFile() {
    if (!editingFileId) return;
    try {
      await updateFile(editingFileId, {
        document_type: editDocType,
        document_type_custom: editDocType === 'other' ? editDocTypeCustom : '',
        serial_numbers: editSerials,
      });
      setEditingFileId(null);
      loadFiles();
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    }
  }

  function toggleSerial(list, setList, snId) {
    setList(list.includes(snId) ? list.filter((id) => id !== snId) : [...list, snId]);
  }

  if (loading) {
    return <div className="flex justify-center py-10"><LoadingSpinner /></div>;
  }

  const canEdit = !['archived'].includes(job.status);

  return (
    <div className="space-y-4">
      {/* Upload area — click or drag and drop */}
      {canEdit && !showUploadForm && (
        <div
          className={`bg-white rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
            dragOver
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input type="file" id="file-upload" className="hidden" onChange={handleFileSelect} />
          <label
            htmlFor="file-upload"
            className="cursor-pointer flex flex-col items-center gap-2"
          >
            <svg className={`w-8 h-8 ${dragOver ? 'text-blue-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            {uploading ? (
              <span className="text-sm font-medium text-blue-600">Uploading...</span>
            ) : dragOver ? (
              <span className="text-sm font-medium text-blue-600">Drop file or link here</span>
            ) : (
              <>
                <span className="text-sm font-medium text-blue-600 hover:text-blue-700">Click to upload or drag and drop</span>
                <span className="text-xs text-gray-400">PDF, images, documents, spreadsheets — or drag a download link</span>
              </>
            )}
          </label>
        </div>
      )}

      {/* Upload form — shown after file is selected */}
      {showUploadForm && pendingFile && (
        <div className="bg-white rounded-lg border border-blue-200 px-4 py-4">
          <div className="flex items-center gap-3 mb-3">
            <FileIcon filename={pendingFile.name} />
            <div>
              <div className="text-sm font-medium text-gray-900">{pendingFile.name}</div>
              <div className="text-xs text-gray-400">{formatFileSize(pendingFile.size)}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            {/* Document type */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Document Type</label>
              <select
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
              >
                {DOCUMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              {docType === 'other' && (
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm mt-1"
                  placeholder="Custom type label"
                  value={docTypeCustom}
                  onChange={(e) => setDocTypeCustom(e.target.value)}
                />
              )}
            </div>

            {/* Serial numbers */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                Link to Serial Numbers <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              {jobSerials.length > 0 ? (
                <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
                  {jobSerials.map((sn) => (
                    <label key={sn.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 rounded px-1">
                      <input
                        type="checkbox"
                        checked={selectedSerials.includes(sn.id)}
                        onChange={() => toggleSerial(selectedSerials, setSelectedSerials, sn.id)}
                        className="rounded border-gray-300"
                      />
                      <span className="font-medium">{sn.serial_number}</span>
                      <span className="text-xs text-gray-400">{sn.part_display} — Unit {sn.unit_number}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 py-2">No serial numbers on this job yet. Add them on the BOM tab.</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleUpload} loading={uploading}>Upload</Button>
            <Button size="sm" variant="ghost" onClick={cancelUpload} disabled={uploading}>Cancel</Button>
          </div>
        </div>
      )}

      {/* File list */}
      {files.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
            <h3 className="text-sm font-medium text-foreground">Job Documents ({files.length})</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {files.map((file) => (
              <div key={file.id}>
                <div className="px-4 py-2 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <FileIcon filename={file.filename || ''} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-foreground truncate">
                        {file.filename || 'Untitled'}
                      </div>
                      <div className="text-xs text-gray-400 flex items-center gap-2 flex-wrap">
                        <span>{formatFileSize(file.size)}</span>
                        <span>&middot;</span>
                        <span>{fmtDate(file.created_at)}</span>
                        {file.uploaded_by_display && (
                          <><span>&middot;</span><span>{file.uploaded_by_display}</span></>
                        )}
                      </div>
                      {/* Document type + serial numbers */}
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        {file.document_type_display && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700">
                            {file.document_type_display}
                          </span>
                        )}
                        {(file.serial_numbers_display || []).map((sn) => (
                          <span key={sn.id} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                            S/N: {sn.serial_number}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    {/* Edit metadata */}
                    {canEdit && (
                      <button
                        onClick={() => editingFileId === file.id ? setEditingFileId(null) : startEditFile(file)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                        title="Edit document type & serial numbers"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => handleDownload(file)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                      title="Download"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </button>
                    {canEdit && (
                      <button
                        onClick={() => handleDelete(file.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Inline edit panel */}
                {editingFileId === file.id && (
                  <div className="px-4 py-3 bg-blue-50/50 border-t border-blue-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">Document Type</label>
                        <select
                          className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                          value={editDocType}
                          onChange={(e) => setEditDocType(e.target.value)}
                        >
                          {DOCUMENT_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                        {editDocType === 'other' && (
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm mt-1"
                            placeholder="Custom type label"
                            value={editDocTypeCustom}
                            onChange={(e) => setEditDocTypeCustom(e.target.value)}
                          />
                        )}
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">Serial Numbers</label>
                        {jobSerials.length > 0 ? (
                          <div className="max-h-28 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1 bg-white">
                            {jobSerials.map((sn) => (
                              <label key={sn.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 rounded px-1">
                                <input
                                  type="checkbox"
                                  checked={editSerials.includes(sn.id)}
                                  onChange={() => toggleSerial(editSerials, setEditSerials, sn.id)}
                                  className="rounded border-gray-300"
                                />
                                <span className="font-medium">{sn.serial_number}</span>
                                <span className="text-xs text-gray-400">{sn.part_display}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 py-1">No serial numbers on this job.</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveEditFile}>Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingFileId(null)}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        !showUploadForm && (
          <EmptyState
            title="No documents"
            description="Upload files related to this job — certs, drawings, inspection reports."
          />
        )
      )}
    </div>
  );
}

function FileIcon({ filename }) {
  const ext = (filename || '').split('.').pop().toLowerCase();
  const isPdf = ext === 'pdf';
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext);
  const isDoc = ['doc', 'docx', 'xls', 'xlsx', 'csv'].includes(ext);
  const color = isPdf ? 'text-red-500' : isImage ? 'text-green-500' : isDoc ? 'text-blue-500' : 'text-gray-400';

  return (
    <svg className={`w-5 h-5 ${color} flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}
